import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import axios from 'axios';
import { showError } from '../actions/notificationActions';
import { dispatchAction } from '../services/authBridge';
import Loading from '../components/Loading';
import TwoFactorScreen from '../components/Auth/TwoFactorScreen';

const AdminUserContext = createContext(null);

// Raw axios instance with NO shared interceptors. Used exclusively for the
// /me and /2fa calls during the bootstrap auth phase so that a 403
// 2fa-required from /me propagates cleanly to AdminUserProvider instead of
// being caught by the shared interceptor (which would try to open the OTP
// modal — but the modal tree isn't mounted yet at this point).
const authRequest = axios.create({
  baseURL: process.env.NODE_ENV === 'production' ? '/api' : '/',
  timeout: 120000,
});

const AdminUserProviderBase = ({ children }) => {
  const dispatchError = (message) => dispatchAction(showError(message));
  const [administrator, setAdministrator] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [needs2fa, setNeeds2fa] = useState(false);

  const clear = useCallback(() => {
    setAdministrator(null);
    setIsAuthenticated(false);
  }, []);

  const fetchMe = useCallback(
    async (isBootstrap) => {
      let redirecting = false;
      let challenged = false;
      try {
        const { data } = await authRequest.get('/me');
        setAdministrator(data.data);
        setIsAuthenticated(true);
        setNeeds2fa(false);
      } catch (err) {
        const status = err && err.response && err.response.status;
        const envelope =
          err && err.response && err.response.data && err.response.data.status;

        if (status === 403 && envelope === '2fa-required') {
          // Surface the 2FA gate instead of the app. Loading stays true so
          // the route tree never renders before auth is complete.
          challenged = true;
          setNeeds2fa(true);
        } else if (status === 401 || status === 403) {
          clear();
          redirecting = true;
          localStorage.removeItem('auth');
          window.location.replace('/');
        } else if (!isBootstrap) {
          // Transient error during a manual refresh — don't clear user state.
          dispatchError('Failed to refresh user profile. Please try again.');
        } else {
          dispatchError(
            'Failed to load user profile. Please refresh the page.'
          );
        }
      } finally {
        if (isBootstrap && !redirecting && !challenged) {
          setIsLoading(false);
        }
      }
    },
    [clear]
  );

  // Called by TwoFactorScreen when the user submits their OTP. Throws on a
  // bad code so the screen can display the server's error message.
  const handle2fa = useCallback(
    async (token) => {
      await authRequest.post('/2fa', { token });
      // Verification succeeded — retry /me. Loading is still true so the user
      // sees the spinner instead of a flash of an empty app shell.
      setNeeds2fa(false);
      await fetchMe(true);
    },
    [fetchMe]
  );

  const cancelAuth = useCallback(async () => {
    try {
      await authRequest.get('/logout');
    } catch {
      // ignore — proceed with local cleanup regardless
    } finally {
      clear();
      localStorage.removeItem('auth');
      window.location.replace('/');
    }
  }, [clear]);

  const refresh = useCallback(() => fetchMe(false), [fetchMe]);

  useEffect(() => {
    fetchMe(true);
  }, [fetchMe]);

  // AdminUserContext.Provider must be present from the very first render so the
  // fiber is established before connected descendants (Sidebar etc.) mount.
  // Introducing a new-context Provider fiber mid-update breaks react-redux v5's
  // legacy context lookup in React 16.x.
  return (
    <AdminUserContext.Provider
      value={{ administrator, isLoading, isAuthenticated, refresh, clear }}
    >
      {needs2fa ? (
        <TwoFactorScreen onSubmit={handle2fa} onCancel={cancelAuth} />
      ) : isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <Loading />
        </div>
      ) : (
        children
      )}
    </AdminUserContext.Provider>
  );
};

export const AdminUserProvider = AdminUserProviderBase;

export const useAdminUser = () => {
  const context = useContext(AdminUserContext);
  if (!context) {
    throw new Error('useAdminUser must be used within AdminUserProvider');
  }
  return context;
};

export default AdminUserContext;
